import React, { useEffect, useState } from "react";
import styles from "./Table.module.css";
import {
	primaryTextColor,
	secondaryTextColor,
	fw400,
	titleS,
	titleM,
} from "../../../Global.module.css";
import ChartType from "../ChartType.json";
import { useDebouncedCallback } from "use-debounce";

const Table = ({ chartType, onChartDataChange, chartData }) => {
	const [tableRows, setTableRows] = useState(
		Math.max(16, chartData.length || 0)
	);
	const [data, setData] = useState([]);

	const debounced = useDebouncedCallback((data) => {
		for (let i = 0; i < data.length; i++) {
			if (
				chartType === ChartType.RangeBar ||
				chartType === ChartType.ColumnLine
			) {
				if (
					!data[i] ||
					(data[i].type === "" &&
						data[i].key === "" &&
						data[i].value[0] === "" &&
						data[i].value[1] === "")
				) {
					data.splice(i, 1);
				}
			} else if (
				!data[i] ||
				(data[i].type === "" &&
					data[i].key === "" &&
					data[i].value === "")
			) {
				data.splice(i, 1);
			}
		}
		onChartDataChange(data);
	}, 2000);

	useEffect(() => {
		if (chartData) {
			setData(chartData);
		}
	}, []);

	useEffect(
		() => () => {
			debounced.flush();
		},
		[debounced]
	);

	const resetTable = () => {
		setData([]);
		onChartDataChange([]);
	};

	const onDataChange = (index, value, field) => {
		let list = [...data];
		let obj = { ...list[index] };
		obj.type = obj.type === undefined ? "" : obj.type;
		obj.key = obj.key === undefined ? "" : obj.key;
		if (
			chartType === ChartType.RangeBar ||
			chartType === ChartType.ColumnLine
		) {
			obj.value = obj.value ? [...obj.value] : [];
		}
		switch (field) {
			case "type":
				obj.type = value;
				break;
			case "key":
				obj.key = value;
				break;
			case "valueOne":
				if (
					chartType === ChartType.RangeBar ||
					chartType === ChartType.ColumnLine
				) {
					obj.value[0] = parseFloat(value);
					if (isNaN(obj.value[0])) {
						obj.value[0] = "";
					}
				} else {
					obj.value = parseFloat(value);
					if (isNaN(obj.value)) {
						obj.value = "";
					}
				}
				break;
			case "valueTwo":
				if (
					chartType === ChartType.RangeBar ||
					chartType === ChartType.ColumnLine
				) {
					obj.value[1] = parseFloat(value);
					if (isNaN(obj.value[1])) {
						obj.value[1] = "";
					}
				}
				break;
			default:
				break;
		}
		list[index] = {
			...obj,
		};
		setData(list);
		debounced.callback(list);
	};

	const getRows = () => {
		const disableSecondInput =
			chartType === ChartType.RangeBar ||
			chartType === ChartType.ColumnLine
				? false
				: true;
		let rows = [];
		for (let i = 0; i < tableRows; i++) {
			rows.push(
				<tr key={i}>
					<td
						className={`${styles.rowSection} ${secondaryTextColor} ${titleS}`}
					>
						{i + 1}
					</td>
					<td className={styles.dataSection}>
						<input
							className={`${styles.tableInput} ${titleM} ${fw400} ${primaryTextColor}`}
							type="text"
							autoComplete="off"
							maxLength={30}
							name="type"
							onChange={(e) =>
								onDataChange(i, e.target.value, "type")
							}
							value={data[i]?.type || ""}
						/>
					</td>
					<td className={styles.dataSection}>
						<input
							className={`${styles.tableInput} ${titleM} ${fw400} ${primaryTextColor}`}
							type="text"
							autoComplete="off"
							maxLength={30}
							name="key"
							onChange={(e) =>
								onDataChange(i, e.target.value, "key")
							}
							value={data[i]?.key || ""}
						/>
					</td>
					{disableSecondInput ? (
						<td className={styles.dataSection}>
							<input
								className={`${styles.tableInput} ${titleM} ${fw400} ${primaryTextColor}`}
								type="number"
								autoComplete="off"
								maxLength={30}
								name="valueOne"
								onChange={(e) =>
									onDataChange(i, e.target.value, "valueOne")
								}
								value={
									data[i]?.value === 0
										? 0
										: data[i]?.value || ""
								}
							/>
						</td>
					) : (
						<td className={styles.dataSection}>
							<input
								className={`${styles.tableInput} ${titleM} ${fw400} ${primaryTextColor}`}
								type="number"
								autoComplete="off"
								maxLength={30}
								name="valueOne"
								onChange={(e) =>
									onDataChange(i, e.target.value, "valueOne")
								}
								value={
									disableSecondInput
										? ""
										: data[i]?.value[0] === 0
										? 0
										: data[i]?.value[0] || ""
								}
							/>
						</td>
					)}

					<td className={styles.dataSection}>
						<input
							className={`${styles.tableInput} ${titleM} ${fw400} ${primaryTextColor}`}
							type="text"
							maxLength={30}
							autoComplete="off"
							name="valueTwo"
							onChange={(e) =>
								onDataChange(i, e.target.value, "valueTwo")
							}
							value={
								disableSecondInput
									? ""
									: data[i]?.value[1] === 0
									? 0
									: data[i]?.value[1] || ""
							}
							disabled={disableSecondInput}
						/>
					</td>
				</tr>
			);
		}
		return rows;
	};

	return (
		<table className={`${styles.table} ${titleS} ${fw400}`}>
			<tbody className={styles.tableBody}>
				<tr>
					<td className={styles.blank}></td>
					<td className={`${styles.colSection} ${primaryTextColor}`}>
						Series
					</td>
					<td className={`${styles.colSection} ${primaryTextColor}`}>
						Key
					</td>
					<td
						className={`${styles.colSection} ${primaryTextColor}`}
						colSpan="2"
					>
						Value
					</td>
				</tr>
				{getRows()}
				<tr>
					<td
						className={`${styles.addSection} ${primaryTextColor}`}
						colSpan="2"
						onClick={resetTable}
					>
						Reset
					</td>
					<td
						className={`${styles.addSection} ${primaryTextColor}`}
						colSpan="3"
						onClick={() => setTableRows(tableRows + 1)}
					>
						+ Add Row
					</td>
				</tr>
			</tbody>
		</table>
	);
};

export default Table;
